import { useState } from 'react';
import { AlertCircle, CheckCircle, Info, XCircle, Bell, Heart, Star, Shield, Car, Upload } from 'lucide-react';

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mb-10">
    <h2 className="text-xl text-[#2D323A] mb-5 pb-2 border-b border-[#E5E7EB]">{title}</h2>
    {children}
  </div>
);

export function DesignSystem() {
  const [switchOn, setSwitchOn] = useState(false);
  const [radio, setRadio] = useState('option1');
  const [checked, setChecked] = useState(true);
  const [tab, setTab] = useState('colors');

  const TABS = ['colors', 'typography', 'buttons', 'inputs', 'components', 'badges', 'alerts'];

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="bg-gradient-to-br from-[#2D323A] to-[#1E2229] text-white py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <span className="text-xs px-2 py-0.5 bg-[#1E6DFF]/30 text-[#1E6DFF] rounded-full mb-3 inline-block">MORROCARS</span>
          <h1 className="text-4xl text-white mb-2">Design System</h1>
          <p className="text-white/70">Color tokens, typography, components, and patterns</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Tab Nav */}
        <div className="flex gap-1 mb-8 flex-wrap">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-xl text-sm capitalize transition-colors ${tab === t ? 'bg-[#1E6DFF] text-white' : 'bg-white border border-[#E5E7EB] text-[#717182] hover:border-[#1E6DFF]'}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === 'colors' && (
          <Section title="Color Palette">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { name: 'Electric Blue', hex: '#1E6DFF', class: 'bg-[#1E6DFF]' },
                { name: 'Dark Gunmetal', hex: '#2D323A', class: 'bg-[#2D323A]' },
                { name: 'Metallic Silver', hex: '#D7D7D7', class: 'bg-[#D7D7D7]' },
                { name: 'Chrome Silver', hex: '#BFC2C7', class: 'bg-[#BFC2C7]' },
                { name: 'Deep Black', hex: '#121212', class: 'bg-[#121212]' },
                { name: 'Success Green', hex: '#22C55E', class: 'bg-[#22C55E]' },
                { name: 'Warning Orange', hex: '#F59E0B', class: 'bg-[#F59E0B]' },
                { name: 'Danger Red', hex: '#EF4444', class: 'bg-[#EF4444]' },
                { name: 'Light Gray', hex: '#F5F7FA', class: 'bg-[#F5F7FA] border border-[#E5E7EB]' },
                { name: 'Border Gray', hex: '#E5E7EB', class: 'bg-[#E5E7EB]' },
              ].map(c => (
                <div key={c.name} className="bg-white rounded-2xl p-3 border border-[#E5E7EB]">
                  <div className={`h-16 rounded-xl mb-2 ${c.class}`} />
                  <p className="text-sm text-[#2D323A]">{c.name}</p>
                  <p className="text-xs text-[#717182]">{c.hex}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {tab === 'typography' && (
          <Section title="Typography">
            <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 space-y-6">
              <div>
                <p className="text-xs text-[#717182] mb-2">Poppins — Heading Font</p>
                <div className="space-y-2">
                  <h1 className="text-[#2D323A]">H1 — 32px Bold Poppins</h1>
                  <h2 className="text-[#2D323A]">H2 — 24px Bold Poppins</h2>
                  <h3 className="text-[#2D323A]">H3 — 20px SemiBold Poppins</h3>
                  <h4 className="text-[#2D323A]">H4 — 16px SemiBold Poppins</h4>
                </div>
              </div>
              <hr className="border-[#E5E7EB]" />
              <div>
                <p className="text-xs text-[#717182] mb-2">Inter — Body Font</p>
                <div className="space-y-2">
                  <p className="text-[#2D323A]">Body Large — 16px Regular Inter</p>
                  <p className="text-sm text-[#717182]">Body Small — 14px Regular Inter</p>
                  <p className="text-xs text-[#BFC2C7]">Caption — 12px Regular Inter</p>
                </div>
              </div>
              <hr className="border-[#E5E7EB]" />
              <div>
                <p className="text-xs text-[#717182] mb-2">Special</p>
                <p className="text-3xl text-[#1E6DFF]">480,000 MAD</p>
                <p className="text-[#22C55E] text-sm">✓ Success text</p>
                <p className="text-[#EF4444] text-sm">✗ Error text</p>
                <p className="text-[#F59E0B] text-sm">⚠ Warning text</p>
              </div>
            </div>
          </Section>
        )}

        {tab === 'buttons' && (
          <Section title="Buttons">
            <div className="space-y-5">
              {[
                { label: 'Primary', buttons: [
                  { label: 'Primary', class: 'bg-[#1E6DFF] text-white hover:bg-[#1557d9]' },
                  { label: 'Primary SM', class: 'bg-[#1E6DFF] text-white hover:bg-[#1557d9] text-sm px-3 py-2' },
                  { label: 'Disabled', class: 'bg-[#1E6DFF] text-white opacity-50 cursor-not-allowed' },
                ]},
                { label: 'Secondary', buttons: [
                  { label: 'Outlined', class: 'border border-[#1E6DFF] text-[#1E6DFF] hover:bg-[#1E6DFF]/5' },
                  { label: 'Ghost', class: 'text-[#1E6DFF] hover:bg-[#1E6DFF]/5' },
                  { label: 'Gray', class: 'border border-[#E5E7EB] text-[#2D323A] hover:bg-[#F5F7FA]' },
                ]},
                { label: 'Status', buttons: [
                  { label: 'Success', class: 'bg-[#22C55E] text-white hover:bg-[#20bd5a]' },
                  { label: 'Warning', class: 'bg-[#F59E0B] text-white hover:opacity-90' },
                  { label: 'Danger', class: 'bg-[#EF4444] text-white hover:opacity-90' },
                ]},
                { label: 'Dark', buttons: [
                  { label: 'Dark', class: 'bg-[#2D323A] text-white hover:bg-[#1E2229]' },
                  { label: 'WhatsApp', class: 'bg-[#25D366] text-white hover:bg-[#20bd5a]' },
                ]},
              ].map(group => (
                <div key={group.label}>
                  <p className="text-xs text-[#717182] mb-2 uppercase tracking-wider">{group.label}</p>
                  <div className="flex flex-wrap gap-3">
                    {group.buttons.map(btn => (
                      <button key={btn.label} className={`px-5 py-2.5 rounded-xl transition-colors ${btn.class}`}>{btn.label}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {tab === 'inputs' && (
          <Section title="Form Controls">
            <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#2D323A] mb-1.5">Default Input</label>
                  <input type="text" placeholder="Enter text..." className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF]" />
                </div>
                <div>
                  <label className="block text-sm text-[#2D323A] mb-1.5">Error State</label>
                  <input type="text" defaultValue="Invalid@" className="w-full px-4 py-3 bg-[#F5F7FA] border-2 border-[#EF4444] rounded-xl focus:outline-none" />
                  <p className="text-xs text-[#EF4444] mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />This field is invalid</p>
                </div>
                <div>
                  <label className="block text-sm text-[#2D323A] mb-1.5">Select / Dropdown</label>
                  <select className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF]">
                    <option>Mercedes-Benz</option>
                    <option>BMW</option>
                    <option>Audi</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[#2D323A] mb-1.5">Textarea</label>
                  <textarea rows={3} placeholder="Enter description..." className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] resize-none" />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input type="checkbox" id="chk1" checked={checked} onChange={e => setChecked(e.target.checked)} className="w-4 h-4 accent-[#1E6DFF]" />
                  <label htmlFor="chk1" className="text-sm text-[#2D323A]">Checkbox — checked</label>
                </div>
                <div className="flex items-center gap-3">
                  <input type="checkbox" id="chk2" className="w-4 h-4 accent-[#1E6DFF]" />
                  <label htmlFor="chk2" className="text-sm text-[#2D323A]">Checkbox — unchecked</label>
                </div>
                <div className="flex gap-4">
                  {['option1', 'option2', 'option3'].map(o => (
                    <label key={o} className="flex items-center gap-2 cursor-pointer text-sm text-[#2D323A]">
                      <input type="radio" name="radioGroup" value={o} checked={radio === o} onChange={() => setRadio(o)} className="accent-[#1E6DFF]" />
                      {o}
                    </label>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#2D323A]">Toggle:</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={switchOn} onChange={e => setSwitchOn(e.target.checked)} className="sr-only peer" />
                    <div className="w-10 h-6 bg-[#E5E7EB] peer-checked:bg-[#1E6DFF] rounded-full relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-4" />
                  </label>
                  <span className="text-sm text-[#717182]">{switchOn ? 'On' : 'Off'}</span>
                </div>
              </div>
            </div>
          </Section>
        )}

        {tab === 'components' && (
          <div className="space-y-8">
            <Section title="Cards">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="bg-[#BFC2C7] h-36" />
                  <div className="p-4">
                    <h3 className="text-[#2D323A]">Vehicle Card</h3>
                    <p className="text-[#1E6DFF] text-sm">480,000 MAD</p>
                    <p className="text-xs text-[#717182] mt-1">2022 · 18,000 km · Diesel</p>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-[#1E6DFF] to-[#1557d9] rounded-2xl p-5 text-white">
                  <h3 className="mb-1">Stat Card</h3>
                  <p className="text-3xl mb-1">1,234</p>
                  <p className="text-sm text-white/70">Total Views</p>
                </div>
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#1E6DFF] rounded-xl flex items-center justify-center text-white">K</div>
                    <div>
                      <p className="text-sm text-[#2D323A]">Karimi Motors</p>
                      <div className="flex items-center gap-0.5">
                        {[1,2,3,4,5].map(i => <Star key={i} className="w-3 h-3 fill-[#F59E0B] text-[#F59E0B]" />)}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-[#717182]">Verified Dealer · Casablanca</p>
                </div>
              </div>
            </Section>

            <Section title="Modals & Overlays">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 shadow-xl max-w-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[#2D323A]">Contact Seller</h3>
                  <button className="w-8 h-8 rounded-xl hover:bg-[#F5F7FA] flex items-center justify-center text-[#717182]">×</button>
                </div>
                <div className="space-y-3">
                  <input placeholder="Your name" className="w-full px-4 py-2.5 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:border-[#1E6DFF]" />
                  <textarea rows={3} placeholder="Message..." className="w-full px-4 py-2.5 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:border-[#1E6DFF] resize-none" />
                  <button className="w-full py-2.5 bg-[#1E6DFF] text-white rounded-xl text-sm hover:bg-[#1557d9]">Send Message</button>
                </div>
              </div>
            </Section>

            <Section title="Progress & Upload">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 space-y-4">
                {[
                  { label: 'Upload Progress', pct: 65, color: 'bg-[#1E6DFF]' },
                  { label: 'Listings Used (3/10)', pct: 30, color: 'bg-[#22C55E]' },
                  { label: 'Profile Complete', pct: 80, color: 'bg-[#F59E0B]' },
                ].map(p => (
                  <div key={p.label}>
                    <div className="flex items-center justify-between mb-1.5 text-sm">
                      <span className="text-[#717182]">{p.label}</span>
                      <span className="text-[#2D323A]">{p.pct}%</span>
                    </div>
                    <div className="h-2 bg-[#F5F7FA] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${p.color}`} style={{ width: `${p.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Pagination">
              <div className="flex items-center gap-2">
                {['←', 1, 2, 3, '...', 8, 9, '→'].map((p, i) => (
                  <button key={i} className={`w-9 h-9 rounded-xl text-sm transition-colors ${p === 2 ? 'bg-[#1E6DFF] text-white' : 'bg-white border border-[#E5E7EB] text-[#717182] hover:border-[#1E6DFF]'}`}>
                    {p}
                  </button>
                ))}
              </div>
            </Section>
          </div>
        )}

        {tab === 'badges' && (
          <Section title="Badges & Tags">
            <div className="space-y-5">
              <div>
                <p className="text-xs text-[#717182] mb-3 uppercase tracking-wider">Status Badges</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Active', class: 'bg-[#22C55E]/10 text-[#22C55E]' },
                    { label: 'Pending', class: 'bg-[#F59E0B]/10 text-[#F59E0B]' },
                    { label: 'Suspended', class: 'bg-[#EF4444]/10 text-[#EF4444]' },
                    { label: 'Draft', class: 'bg-[#E5E7EB] text-[#717182]' },
                    { label: 'Featured', class: 'bg-[#1E6DFF] text-white' },
                    { label: 'Verified', class: 'bg-[#1E6DFF]/10 text-[#1E6DFF]' },
                    { label: 'Hot Deal', class: 'bg-[#EF4444] text-white' },
                    { label: 'New', class: 'bg-[#22C55E] text-white' },
                  ].map(b => (
                    <span key={b.label} className={`px-2.5 py-1 rounded-lg text-sm ${b.class}`}>{b.label}</span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-[#717182] mb-3 uppercase tracking-wider">Category Tags</p>
                <div className="flex flex-wrap gap-2">
                  {['Car', 'SUV', 'Motorcycle', 'ATV', 'RV', 'Commercial', 'Truck', 'Van'].map(cat => (
                    <span key={cat} className="px-3 py-1.5 bg-[#F5F7FA] border border-[#E5E7EB] text-[#717182] rounded-xl text-sm flex items-center gap-1">
                      <Car className="w-3 h-3" />{cat}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Section>
        )}

        {tab === 'alerts' && (
          <Section title="Alerts & Toasts">
            <div className="space-y-3">
              {[
                { icon: Info, label: 'Information', desc: 'This is an informational message.', class: 'bg-[#1E6DFF]/5 border-[#1E6DFF]/20 text-[#1E6DFF]' },
                { icon: CheckCircle, label: 'Success', desc: 'Your action completed successfully.', class: 'bg-[#22C55E]/5 border-[#22C55E]/20 text-[#22C55E]' },
                { icon: AlertCircle, label: 'Warning', desc: 'Please review this before proceeding.', class: 'bg-[#F59E0B]/5 border-[#F59E0B]/20 text-[#F59E0B]' },
                { icon: XCircle, label: 'Error', desc: 'An error occurred. Please try again.', class: 'bg-[#EF4444]/5 border-[#EF4444]/20 text-[#EF4444]' },
              ].map(a => {
                const Icon = a.icon;
                return (
                  <div key={a.label} className={`flex items-center gap-3 p-4 rounded-xl border ${a.class}`}>
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <div>
                      <p className="text-sm">{a.label}: {a.desc}</p>
                    </div>
                  </div>
                );
              })}

              <div className="mt-6">
                <p className="text-xs text-[#717182] mb-3 uppercase tracking-wider">Toast Notifications</p>
                <div className="space-y-2 max-w-sm">
                  {[
                    { msg: 'Listing published successfully!', icon: CheckCircle, class: 'bg-[#2D323A] text-white' },
                    { msg: 'New lead from Mohammed A.', icon: Bell, class: 'bg-[#1E6DFF] text-white' },
                    { msg: 'Payment failed. Try again.', icon: XCircle, class: 'bg-[#EF4444] text-white' },
                  ].map((t, i) => {
                    const Icon = t.icon;
                    return (
                      <div key={i} className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg ${t.class}`}>
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm">{t.msg}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}
